/**
 * UTM 파라미터 저장/조회 헬퍼 (last-touch 모델).
 *
 * 유입될 때마다 '이번 방문'의 UTM 으로 저장소를 **덮어쓴다**(과거 유입과 병합하지 않음).
 * 링크 전파(문의 폼)와 분석 이벤트 모두 마지막 유입(last-touch)만 사용하므로 유입끼리
 * 섞이지 않고, 단일 timestamp + 2주 TTL 로 stale attribution 도 자연히 정리된다.
 *
 * TODO(utm-shared): packages/shared 출범 후 이 파일을 제거하고
 * @/shared/utm 으로 이관. web-app / play-app / quiz-app 3개 복사본을
 * 하나로 통합 필요. 현재는 logger 통합 작업과의 충돌을 피하기 위해
 * 앱별 복사본을 유지함.
 */
import { safeLocalStorage } from '@toss/storage';

const UTM_STORAGE_KEY = 'utm_data';
const UTM_TIMESTAMP_KEY = 'utm_timestamp';
/**
 * 저장 스키마 버전. 이 값이 찍힌 utm_data 는 last-touch(원자적 단일 유입)로 저장된 것이라
 * 값에 쉼표가 있어도(예: utm_campaign=spring,summer) 그대로 신뢰한다. 이 마커가 없는
 * 레코드는 이 기능 이전(콤마 병합 가능)의 레거시로 간주한다.
 */
const UTM_SCHEMA_VERSION_KEY = 'utm_v';
const UTM_SCHEMA_VERSION = '2';

/**
 * 저장된 UTM 의 유효기간(2주). 광고 attribution window 표준을 따르며,
 * 마지막 유입 시각(utm_timestamp)으로부터 이 기간이 지나면 stale 로 보고 자동 비운다.
 */
const UTM_TTL_MS = 14 * 24 * 60 * 60 * 1000;

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  fbclid?: string;
  gclid?: string;
  ttclid?: string;
}

/** UTM/클릭ID 키 목록 */
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fbclid',
  'gclid',
  'ttclid',
] as const satisfies readonly (keyof UTMParams)[];

export const getUTMParams = (): UTMParams => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
    fbclid: params.get('fbclid') || undefined,
    gclid: params.get('gclid') || undefined,
    ttclid: params.get('ttclid') || undefined,
  };
};

/** utm_data / utm_timestamp / 스키마 버전을 함께 비운다. */
const clearStoredUTM = () => {
  safeLocalStorage.remove(UTM_STORAGE_KEY);
  safeLocalStorage.remove(UTM_TIMESTAMP_KEY);
  safeLocalStorage.remove(UTM_SCHEMA_VERSION_KEY);
};

/**
 * 저장된 UTM 이 만료됐는지 판정한다.
 * timestamp 가 없거나(이 기능 이전 저장된 레거시 데이터) 숫자로 유효하지 않으면 보존기간을
 * 알 수 없으므로 만료로 간주한다(무기한 잔존 방지).
 */
const isStoredUTMExpired = () => {
  const timestamp = Number(safeLocalStorage.get(UTM_TIMESTAMP_KEY));
  return (
    !Number.isFinite(timestamp) ||
    timestamp <= 0 ||
    Date.now() - timestamp > UTM_TTL_MS
  );
};

export const getStoredUTMParams = () => {
  const result = {
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    fbclid: '',
    gclid: '',
    ttclid: '',
  };
  try {
    const utmData = safeLocalStorage.get(UTM_STORAGE_KEY);
    if (!utmData) return result;

    // 2주(TTL) 지난 유입은 만료 처리하고 저장소에서 비운다.
    if (isStoredUTMExpired()) {
      clearStoredUTM();
      return result;
    }

    const parsedData = JSON.parse(utmData) as UTMParams;
    const isCurrentSchema =
      safeLocalStorage.get(UTM_SCHEMA_VERSION_KEY) === UTM_SCHEMA_VERSION;

    if (isCurrentSchema) {
      // 이 기능 이후 last-touch(원자적 단일 유입)로 저장된 값 → 값에 쉼표가 있어도 신뢰
      UTM_KEYS.forEach(key => {
        result[key] = parsedData[key] || '';
      });
      return result;
    }

    // 레거시(스키마 마커 없음): 유입 병합 여부를 알 수 없으므로, 채워진 키가 정확히 1개인
    // 명백한 단일 유입 레코드만 승계하고 그 외(여러 키 = 조합 위험)는 승계하지 않는다.
    // (어차피 다음 유입 때 현재 스키마로 덮어써진다)
    const populated = UTM_KEYS.filter(key => parsedData[key]);
    if (populated.length === 1) {
      const key = populated[0];
      result[key] = parsedData[key] || '';
    }
    return result;
  } catch (_e) {
    return result;
  }
};

/**
 * 링크(외부/내부)에 유입 UTM/클릭ID 파라미터를 붙여 반환한다.
 * - **유입(visit) 단위로 소스를 고른다**: 현재 URL 에 attribution 값이 하나라도 있으면 URL 값만,
 *   전혀 없으면 저장된 last-touch 값(2주 TTL)만 사용한다. 키 단위로 섞지 않는다.
 * - 광고 진입 후 페이지를 이동해 주소창에서 utm 이 사라져도 저장값 폴백으로 유입 출처가 유지된다.
 * - 저장/제거를 하지 않는 읽기 전용이라 같은 링크를 여러 번 만들어도 결과가 동일하다(idempotent).
 * - 외부 문의 폼(Salesmap/tally 등)은 우리 localStorage 를 못 읽으므로 URL 로 실어 보내야 한다.
 * - SSR 이거나 URL 파싱에 실패하면 원본 문자열을 그대로 돌려준다.
 */
export const appendUTMParams = (url: string): string => {
  if (typeof window === 'undefined') return url;
  try {
    const urlObj = new URL(url, window.location.origin);
    const current = getUTMParams();
    // 현재 방문에 attribution 값이 하나라도 있으면 이번 방문 값만 쓰고,
    // 없을 때만 저장된 last-touch 로 폴백한다(유입끼리 혼합 금지).
    const hasCurrentUtm = UTM_KEYS.some(key => current[key]);
    const source = hasCurrentUtm ? current : getStoredUTMParams();

    UTM_KEYS.forEach(key => {
      const value = source[key];
      if (value) {
        urlObj.searchParams.set(key, value);
      }
    });

    return urlObj.toString();
  } catch {
    // 잘못된 URL 등 파싱 실패 시 원본 유지 (링크 자체는 깨지지 않도록)
    return url;
  }
};

/**
 * 이번 유입의 UTM 으로 저장소를 덮어쓴다(last-touch, 과거 유입과 병합하지 않음).
 */
export const saveUTMParams = (utmParams: UTMParams): UTMParams => {
  safeLocalStorage.set(UTM_STORAGE_KEY, JSON.stringify(utmParams));
  safeLocalStorage.set(UTM_TIMESTAMP_KEY, Date.now().toString());
  // 현재 스키마로 저장됨을 표시(값의 쉼표를 레거시로 오판하지 않도록)
  safeLocalStorage.set(UTM_SCHEMA_VERSION_KEY, UTM_SCHEMA_VERSION);

  return utmParams;
};
